import { useState } from 'react';
import toast from 'react-hot-toast';

import { type AgentSession, resumeAgentSession } from '@/api-models/agent-sessions';
import { Button } from '@/ui/button/Button';
import { Spinner } from '@/ui/Spinner';

import { type StatusTone, sessionStatusLine } from '../session-status';

const TONE_TEXT: Record<StatusTone, string> = {
  info: 'text-info',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
  neutral: 'text-fg-tertiary',
};

/**
 * The sandbox lifecycle line above the chatbox — provisioning progress, ready,
 * paused, or failed.
 *
 * Fixed height so the composer footer doesn't jump as the line cycles through
 * provisioning steps.
 */
export function SandboxStatusLine({
  session,
  attachHint,
}: {
  session: AgentSession;
  /**
   * Socket-level status, which outranks "Sandbox ready" when set: the sandbox
   * can be running while the bridge to the agent is still attaching or retrying,
   * and saying "ready" through that gap reads as a hung agent.
   */
  attachHint?: string | null;
}) {
  const base = sessionStatusLine(session);
  // Both settled phases: `ready` (sandbox up) and `planning` (no sandbox to bring up). In either,
  // a socket that hasn't attached is the more useful thing to say — during provisioning it isn't,
  // because the progress steps already explain the wait.
  const settled = base.phase === 'ready' || base.phase === 'planning';
  const line = attachHint && settled ? { ...base, text: attachHint, tone: 'info' as const, spinner: true } : base;
  const [resuming, setResuming] = useState(false);

  const resume = async () => {
    setResuming(true);
    try {
      await resumeAgentSession(session.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to resume the sandbox');
    } finally {
      setResuming(false);
    }
  };

  return (
    <div className="flex min-h-5 items-center gap-1.5 px-1 text-2xs">
      {line.spinner && <Spinner size={4} />}
      <span className={TONE_TEXT[line.tone]}>{line.text}</span>
      {line.detail && (
        <span className="min-w-0 truncate text-fg-tertiary" title={line.detail}>
          {line.detail}
        </span>
      )}
      {line.action && (
        <span className="ml-auto">
          <Button variant="ghost" size={7} onClick={resume} isLoading={resuming}>
            {line.action}
          </Button>
        </span>
      )}
    </div>
  );
}
